import { StateCreator } from 'zustand';
import type { AppState } from '../../store/app-state';
import { SoundClip, SoundCue, MicAssignment, MicSwap, MicPackConfig } from '../../types';
import { logger } from '../../utils/logger.ts';
import { get, set as idbSet } from 'idb-keyval';
import { getScenesInOrder, buildActorScenePresence, sortActorsByAppearance, generateSwapSuggestions } from './mic-swap-utils';
import {
    deleteMicAssignment,
    deleteMicSwap,
    deleteSoundClip,
    deleteSoundCue,
} from './sound-api.ts';
import type { ProjectScope } from '../sync/project-scope.ts';
import { requestFileAccess } from '../files/service';
import {
    markPendingMicAssignmentDelete,
    markPendingMicAssignmentWrite,
    markPendingMicPackConfigWrite,
    markPendingMicSwapDelete,
    markPendingMicSwapWrite,
    markPendingSoundClipDelete,
    markPendingSoundClipWrite,
    markPendingSoundCueDelete,
    markPendingSoundCueWrite,
} from '../sync/sound-pending-writes';

export interface SoundSlice {
    soundClips: SoundClip[];
    soundCues: SoundCue[];
    micAssignments: MicAssignment[];
    micSwaps: MicSwap[];
    micPackConfig: MicPackConfig;
    soundActions: {
        // Clips
        addSoundClip: (clip: Partial<SoundClip>, blob?: Blob) => Promise<string>;
        updateSoundClip: (id: string, updates: Partial<SoundClip>) => void;
        deleteSoundClip: (id: string) => void;
        getSoundBlob: (id: string) => Promise<Blob | undefined>;
        
        // Cues
        addSoundCue: (cue: Partial<SoundCue>) => string;
        updateSoundCue: (id: string, updates: Partial<SoundCue>) => void;
        deleteSoundCue: (id: string) => void;
        reorderSoundCues: (sceneId: string, oldIndex: number, newIndex: number) => void;

        // Mic Packs
        setMicPackConfig: (config: Partial<MicPackConfig>) => void;
        generateMicAssignments: () => void;
        addMicAssignment: (assignment: Partial<MicAssignment>) => void;
        updateMicAssignment: (id: string, updates: Partial<MicAssignment>) => void;
        deleteMicAssignment: (id: string) => void;
        reorderMicAssignment: (oldPackNumber: number, newPackNumber: number) => void;

        // Mic Swaps
        addMicSwap: (swap: Partial<MicSwap>) => void;
        updateMicSwap: (id: string, updates: Partial<MicSwap>) => void;
        deleteMicSwap: (id: string) => void;
        clearMicSwaps: () => void;
        
        // Project scoping
        clearSound: () => void;
    };
}

function getSoundProjectScope(state: AppState): ProjectScope | null {
    if (!state.cloud.enabled || !state.cloud.orgId || !state.cloud.projectId) {
        return null;
    }
    return { orgId: state.cloud.orgId, projectId: state.cloud.projectId };
}

function preserveSoundDelete(operation: Promise<void>, label: string): void {
    void operation.catch(error => {
        logger.error(`[sound-store] Failed to preserve ${label}:`, error);
    });
}

async function fetchRepositorySoundBlob(
    state: AppState,
    clip: SoundClip | undefined,
    cacheKey: string
): Promise<Blob | undefined> {
    if (!clip?.repositoryFileId) {
        return;
    }

    const scope = getSoundProjectScope(state);
    if (!scope) {
        return;
    }

    const access = await requestFileAccess(
        scope,
        clip.repositoryFileId,
        'preview',
        clip.repositoryStorageRef ? { storageRef: clip.repositoryStorageRef } : undefined
    );
    const response = await fetch(access.url);
    if ('ok' in response && response.ok === false) {
        throw new Error(`Failed to load repository sound file: ${response.status}`);
    }

    const blob = await response.blob();
    await idbSet(cacheKey, blob);
    return blob;
}

export const createSoundSlice: StateCreator<
    AppState & SoundSlice,
    [["zustand/immer", never]],
    [],
    SoundSlice
> = (set, getStore) => ({
    soundClips: [],
    soundCues: [],
    micAssignments: [],
    micSwaps: [],
    micPackConfig: { totalPacks: 0 },
    soundActions: {
        addSoundClip: async (clip, blob) => {
            const id = crypto.randomUUID();
            let localKey = clip.localKey;
            
            if (blob) {
                localKey = `sound_blob_${id}`;
                await idbSet(localKey, blob);
            }

            set(state => {
                if (!state.soundClips) state.soundClips = [];
                state.soundClips.push({
                    id,
                    name: 'New Sound',
                    volume: 1.0,
                    loop: false,
                    trimStart: 0,
                    localKey,
                    ...clip
                } as SoundClip);
            });
            const scope = getSoundProjectScope(getStore());
            if (scope) {
                markPendingSoundClipWrite(scope, id);
            }
            return id;
        },
        updateSoundClip: (id, updates) => {
            const scope = getSoundProjectScope(getStore());
            let didUpdate = false;
            set(state => {
                const clip = state.soundClips.find(c => c.id === id);
                if (clip) {
                    Object.assign(clip, updates);
                    didUpdate = true;
                }
            });
            if (scope && didUpdate) {
                markPendingSoundClipWrite(scope, id);
            }
        },
        deleteSoundClip: (id) => {
            const scope = getSoundProjectScope(getStore());
            let didDelete = false;
            const affectedCueIds: string[] = [];
            set(state => {
                didDelete = state.soundClips.some(c => c.id === id);
                state.soundClips = state.soundClips.filter(c => c.id !== id);
                for (const cue of state.soundCues) {
                    if (cue.soundClipId === id) {
                        cue.soundClipId = undefined;
                        affectedCueIds.push(cue.id);
                    }
                }
            });
            if (scope && didDelete) {
                markPendingSoundClipDelete(scope, id);
                preserveSoundDelete(deleteSoundClip(scope, id), 'sound clip deletion');
                for (const cueId of affectedCueIds) {
                    markPendingSoundCueWrite(scope, cueId);
                }
            }
        },
        getSoundBlob: async (id) => {
             // Access state via getStore() if needed, but here we need to find the clip to get localKey
             const state = getStore();
             const clip = state.soundClips?.find(c => c.id === id);
             const key = clip?.localKey || `sound_blob_${id}`;
             const cachedBlob = await get<Blob>(key);
             return cachedBlob || await fetchRepositorySoundBlob(state, clip, key);
        },
        addSoundCue: (cue) => {
            const id = crypto.randomUUID();
            set(state => {
                if (!state.soundCues) state.soundCues = [];
                // Determine max sort order for the scene
                const sceneCues = state.soundCues.filter(c => c.sceneId === cue.sceneId && !c.deleted);
                const maxOrder = sceneCues.reduce((max, c) => (c.sortOrder > max ? c.sortOrder : max), -1);

                state.soundCues.push({
                    id,
                    sceneId: cue.sceneId || '',
                    cueNumber: '',
                    cueLine: '',
                    description: '',
                    sortOrder: maxOrder + 1,
                    ...cue
                });
            });
            const scope = getSoundProjectScope(getStore());
            if (scope) {
                markPendingSoundCueWrite(scope, id);
            }
            return id;
        },
        updateSoundCue: (id, updates) => {
            const scope = getSoundProjectScope(getStore());
            let didUpdate = false;
            set(state => {
                const cue = state.soundCues.find(c => c.id === id);
                if (cue) {
                    Object.assign(cue, updates);
                    didUpdate = true;
                }
            });
            if (scope && didUpdate) {
                markPendingSoundCueWrite(scope, id);
            }
        },
        deleteSoundCue: (id) => {
            const scope = getSoundProjectScope(getStore());
            let didDelete = false;
            set(state => {
                didDelete = state.soundCues.some(c => c.id === id);
                state.soundCues = state.soundCues.filter(c => c.id !== id);
            });
            if (scope && didDelete) {
                markPendingSoundCueDelete(scope, id);
                preserveSoundDelete(deleteSoundCue(scope, id), 'sound cue deletion');
            }
        },
        reorderSoundCues: (sceneId, oldIndex, newIndex) => {
            const scope = getSoundProjectScope(getStore());
            const reorderedCueIds: string[] = [];
            set(state => {
                const sceneCues = state.soundCues
                    .filter(c => c.sceneId === sceneId && !c.deleted)
                    .toSorted((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

                if (oldIndex < 0 || newIndex < 0 || oldIndex >= sceneCues.length || newIndex >= sceneCues.length) return;

                // Reordering logic
                const [moved] = sceneCues.splice(oldIndex, 1);
                sceneCues.splice(newIndex, 0, moved!);

                // Updates in main array
                for (const [index, cue] of sceneCues.entries()) {
                    const target = state.soundCues.find(c => c.id === cue.id);
                    if (target) {
                        target.sortOrder = index;
                        reorderedCueIds.push(cue.id);
                    }
                }
            });
            if (scope) {
                for (const id of reorderedCueIds) {
                    markPendingSoundCueWrite(scope, id);
                }
            }
        },

        // ── Mic Pack Actions ──────────────────────────────────────────────

        setMicPackConfig: (config) => {
            const scope = getSoundProjectScope(getStore());
            set(state => {
                Object.assign(state.micPackConfig, config);
            });
            if (scope) {
                markPendingMicPackConfigWrite(scope);
            }
        },

        generateMicAssignments: () => {
            const scope = getSoundProjectScope(getStore());
            const previousAssignmentIds = new Set(getStore().micAssignments.map(assignment => assignment.id));
            const previousSwapIds = new Set(getStore().micSwaps.map(swap => swap.id));
            const nextAssignmentIds: string[] = [];
            const nextSwapIds: string[] = [];
            set(state => {
                const fullState = getStore();
                const { showStructure, personnel } = fullState;
                const characters = showStructure.characters || [];
                const acts = showStructure.acts || [];
                const scenes = showStructure.scenes || [];

                // 1. Get scene order
                const sceneOrder = getScenesInOrder(acts, scenes);

                // 2. Build actor → scene presence map
                const actorScenePresence = buildActorScenePresence(characters, personnel, fullState.castAssignments || []);

                // 3. Get all actor IDs that have character assignments
                const micEligibleActorIds = Array.from(actorScenePresence.keys());

                // 4. Sort by first appearance
                const sortedActorIds = sortActorsByAppearance(
                    micEligibleActorIds,
                    actorScenePresence,
                    sceneOrder
                );

                // 5. Preserve existing locked assignments
                const existingLocked = state.micAssignments.filter(a => a.lockState === 'locked');
                const lockedActorIds = new Set(existingLocked.map(a => a.actorId));

                // 6. Build new assignments
                const newAssignments: MicAssignment[] = [];
                let packNumber = 1;

                // Locked actors get dedicated packs first (preserve their existing pack numbers)
                for (const locked of existingLocked) {
                    newAssignments.push({
                        ...locked,
                        packNumber: packNumber++,
                    });
                }

                // Remaining actors get packs in appearance order
                const totalPacks = state.micPackConfig.totalPacks;
                const unlockedActorIds: string[] = [];

                for (const actorId of sortedActorIds) {
                    if (lockedActorIds.has(actorId)) continue;

                    if (packNumber <= totalPacks) {
                        // Find existing assignment to preserve editable fields
                        const existing = state.micAssignments.find(a => a.actorId === actorId);
                        newAssignments.push({
                            id: existing?.id || crypto.randomUUID(),
                            packNumber: packNumber++,
                            actorId,
                            lockState: existing?.lockState || 'normal',
                            frequency: existing?.frequency || '',
                            batteryType: existing?.batteryType || 'AA',
                            batteryLevel: existing?.batteryLevel || 'Fresh',
                            batteryCustomLabel: existing?.batteryCustomLabel,
                            lastBatteryChange: existing?.lastBatteryChange || null,
                            notes: existing?.notes || '',
                        });
                    } else {
                        unlockedActorIds.push(actorId);
                    }
                }

                state.micAssignments = newAssignments;
                nextAssignmentIds.push(...newAssignments.map(assignment => assignment.id));

                // 7. Generate swap suggestions for un-packed actors
                if (unlockedActorIds.length > 0) {
                    const { swaps } = generateSwapSuggestions(
                        newAssignments,
                        unlockedActorIds,
                        actorScenePresence,
                        sceneOrder
                    );
                    state.micSwaps = swaps;
                } else {
                    state.micSwaps = [];
                }
                nextSwapIds.push(...state.micSwaps.map(swap => swap.id));
            });
            if (scope) {
                for (const id of nextAssignmentIds) {
                    markPendingMicAssignmentWrite(scope, id);
                }
                for (const id of previousAssignmentIds) {
                    if (!nextAssignmentIds.includes(id)) {
                        markPendingMicAssignmentDelete(scope, id);
                        preserveSoundDelete(deleteMicAssignment(scope, id), 'mic assignment deletion');
                    }
                }
                for (const id of nextSwapIds) {
                    markPendingMicSwapWrite(scope, id);
                }
                for (const id of previousSwapIds) {
                    if (!nextSwapIds.includes(id)) {
                        markPendingMicSwapDelete(scope, id);
                        preserveSoundDelete(deleteMicSwap(scope, id), 'mic swap deletion');
                    }
                }
            }
        },

        addMicAssignment: (assignment) => {
            const id = crypto.randomUUID();
            set(state => {
                if (!state.micAssignments) state.micAssignments = [];
                state.micAssignments.push({
                    id,
                    packNumber: state.micAssignments.length + 1,
                    actorId: '',
                    lockState: 'normal',
                    frequency: '',
                    batteryType: 'AA',
                    batteryLevel: 'Fresh',
                    lastBatteryChange: null,
                    notes: '',
                    ...assignment,
                } as MicAssignment);
            });
            const scope = getSoundProjectScope(getStore());
            if (scope) {
                markPendingMicAssignmentWrite(scope, id);
            }
        },

        updateMicAssignment: (id, updates) => {
            const scope = getSoundProjectScope(getStore());
            let didUpdate = false;
            set(state => {
                const assignment = state.micAssignments.find(a => a.id === id);
                if (!assignment) return;

                // Auto-set lastBatteryChange when battery is set to Fresh
                if (updates.batteryLevel === 'Fresh' && assignment.batteryLevel !== 'Fresh') {
                    updates.lastBatteryChange = new Date().toISOString();
                }

                Object.assign(assignment, updates);
                didUpdate = true;
            });
            if (scope && didUpdate) {
                markPendingMicAssignmentWrite(scope, id);
            }
        },

        deleteMicAssignment: (id) => {
            const scope = getSoundProjectScope(getStore());
            let didDelete = false;
            const renumberedAssignmentIds: string[] = [];
            set(state => {
                didDelete = state.micAssignments.some(a => a.id === id);
                state.micAssignments = state.micAssignments.filter(a => a.id !== id);
                // Renumber remaining packs
                const sortedAssignments = [...state.micAssignments]
                    .toSorted((a, b) => a.packNumber - b.packNumber);
                for (const [i, a] of sortedAssignments.entries()) {
                    const target = state.micAssignments.find(m => m.id === a.id);
                    if (target) {
                        target.packNumber = i + 1;
                        renumberedAssignmentIds.push(target.id);
                    }
                }
            });
            if (scope && didDelete) {
                markPendingMicAssignmentDelete(scope, id);
                preserveSoundDelete(deleteMicAssignment(scope, id), 'mic assignment deletion');
                for (const assignmentId of renumberedAssignmentIds) {
                    markPendingMicAssignmentWrite(scope, assignmentId);
                }
            }
        },

        reorderMicAssignment: (oldPackNumber, newPackNumber) => {
            const scope = getSoundProjectScope(getStore());
            const affectedAssignmentIds = new Set<string>();
            set(state => {
                const assignment = state.micAssignments.find(a => a.packNumber === oldPackNumber);
                if (!assignment) return;

                // Shift other assignments
                if (oldPackNumber < newPackNumber) {
                    const shiftedAssignments = state.micAssignments
                        .filter(a => a.packNumber > oldPackNumber && a.packNumber <= newPackNumber);
                    for (const a of shiftedAssignments) {
                        a.packNumber--;
                        affectedAssignmentIds.add(a.id);
                    }
                } else {
                    const shiftedAssignments = state.micAssignments
                        .filter(a => a.packNumber >= newPackNumber && a.packNumber < oldPackNumber);
                    for (const a of shiftedAssignments) {
                        a.packNumber++;
                        affectedAssignmentIds.add(a.id);
                    }
                }
                assignment.packNumber = newPackNumber;
                affectedAssignmentIds.add(assignment.id);
            });
            if (scope) {
                for (const id of affectedAssignmentIds) {
                    markPendingMicAssignmentWrite(scope, id);
                }
            }
        },

        // ── Mic Swap Actions ──────────────────────────────────────────────

        addMicSwap: (swap) => {
            const id = crypto.randomUUID();
            set(state => {
                if (!state.micSwaps) state.micSwaps = [];
                state.micSwaps.push({
                    id,
                    packNumber: 0,
                    fromActorId: '',
                    toActorId: '',
                    afterSceneId: '',
                    isAutoSuggested: true,
                    isConfirmed: false,
                    ...swap,
                } as MicSwap);
            });
            const scope = getSoundProjectScope(getStore());
            if (scope) {
                markPendingMicSwapWrite(scope, id);
            }
        },

        updateMicSwap: (id, updates) => {
            const scope = getSoundProjectScope(getStore());
            let didUpdate = false;
            set(state => {
                const swap = state.micSwaps.find(s => s.id === id);
                if (swap) {
                    Object.assign(swap, updates);
                    didUpdate = true;
                }
            });
            if (scope && didUpdate) {
                markPendingMicSwapWrite(scope, id);
            }
        },

        deleteMicSwap: (id) => {
            const scope = getSoundProjectScope(getStore());
            let didDelete = false;
            set(state => {
                didDelete = state.micSwaps.some(s => s.id === id);
                state.micSwaps = state.micSwaps.filter(s => s.id !== id);
            });
            if (scope && didDelete) {
                markPendingMicSwapDelete(scope, id);
                preserveSoundDelete(deleteMicSwap(scope, id), 'mic swap deletion');
            }
        },

        clearMicSwaps: () => {
            const scope = getSoundProjectScope(getStore());
            const clearedSwapIds: string[] = [];
            set(state => {
                clearedSwapIds.push(...state.micSwaps.map(swap => swap.id));
                state.micSwaps = [];
            });
            if (scope) {
                for (const id of clearedSwapIds) {
                    markPendingMicSwapDelete(scope, id);
                    preserveSoundDelete(deleteMicSwap(scope, id), 'mic swap deletion');
                }
            }
        },
        
        clearSound: () => set(state => {
            state.soundClips = [];
            state.soundCues = [];
            state.micAssignments = [];
            state.micSwaps = [];
            state.micPackConfig = { totalPacks: 0 };
        })
    }
});
